import React, { useEffect, useState } from 'react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../../common/SafeIcon'
import { supabase, supabaseConfigError } from '../../../config/supabase'
import CurriculumImportExport from './CurriculumImportExport'

const { FiUpload, FiBookOpen, FiRefreshCw, FiAlertTriangle } = FiIcons

const CurriculumImportExportPage = () => {
  const [curricula, setCurricula] = useState([])
  const [selectedCurriculumId, setSelectedCurriculumId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadCurricula = async () => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const { data, error: supabaseError } = await supabase
        .from('curricula')
        .select('*')
        .order('created_at', { ascending: false })

      if (supabaseError) throw supabaseError

      setCurricula(data || [])
      setSelectedCurriculumId((data && data[0]?.id) || '')
    } catch (loadError) {
      console.error('Error loading curricula', loadError)
      setError('Unable to load curricula right now. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCurricula()
  }, [])

  const selectedCurriculum = curricula.find((curriculum) => curriculum.id === selectedCurriculumId)

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm uppercase tracking-wider text-gray-500">Curriculum Builder</p>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <SafeIcon icon={FiUpload} className="w-8 h-8 text-[#00AAFF]" />
            Import &amp; Export
          </h1>
          <p className="text-gray-600 mt-2">
            Move curricula between classrooms with quick import and export tools.
          </p>
        </div>
        <button
          onClick={loadCurricula}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 hover:border-[#00AAFF] hover:text-[#00AAFF] transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4 sm:p-6 border border-gray-100 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex flex-col gap-2 w-full sm:max-w-sm">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <SafeIcon icon={FiBookOpen} className="w-4 h-4 text-[#00AAFF]" />
              Select curriculum
            </span>
            <select
              value={selectedCurriculumId}
              onChange={(e) => setSelectedCurriculumId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              disabled={loading || !curricula.length}
            >
              {!curricula.length && <option value="">No curricula available</option>}
              {curricula.map((curriculum) => (
                <option key={curriculum.id} value={curriculum.id}>
                  {curriculum.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            <div className="animate-pulse h-12 bg-gray-100 rounded-lg" />
            <div className="animate-pulse h-64 bg-gray-100 rounded-lg" />
          </div>
        ) : selectedCurriculum ? (
          <CurriculumImportExport
            curriculum={selectedCurriculum}
            onImported={loadCurricula}
          />
        ) : (
          <div className="text-center py-12 text-gray-600 border border-dashed border-gray-200 rounded-lg">
            <p className="font-semibold mb-1">No curriculum selected</p>
            <p className="text-sm text-gray-500">Create a curriculum first to access import and export tools.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default CurriculumImportExportPage
